import { Tabs, Modal, notification, Spin } from 'antd';
import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { AlertMessages, NOTIFICATION_MESSAGES, API_ENDPOINTS, API_BASE_URL, SWITCH_TEXT } from '../../../../constants/Constants.js';
import { NextButton, ResetButton, SaveButton } from '../../../../components/common/Button';
import { setreportingData, setactiveTabKey, setisSaved, resetSwitchState, resetReportForm, resetEmissionSaved, setisReset , setLoading, populateReportForm} from '../../../../redux/slices/supplierReportingSlice';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import TabsGenerator from './TabsGenerator';
import useApiFetch from '../../../../hooks/useApiFetch';
import "./Report.css"

const { confirm } = Modal;

const Report = () => {
  const dispatch = useDispatch();
  const { activeTabKey, reportingData, isLoading, isSaved, ReportForm, emissionSaved, SwitchState, isCalculated, isQuestionCalculated } = useSelector((state) => state.reporting);
  const { savedFocusAreas, isPresurveySubmitted } = useSelector((state) => state.presurvey);
  const supplier_id = sessionStorage.getItem("supplierId");
  const emailId=sessionStorage.getItem("Email");
  const reportingPillarLength = Object.values(savedFocusAreas).filter(value => value === true).length;

  // New state to track saved pillars
  const [savedPillars, setSavedPillars] = useState({});
  // Add state to track if data is being loaded from API
  const [isPreloadingData, setIsPreloadingData] = useState(false);

  notification.config({
    maxCount: 1
  });
  const canSave = (() => {
    if (activeTabKey === "Waste") return true;
    const tabStatus = isQuestionCalculated?.[activeTabKey];
    if (!tabStatus) return false;

    return Object.values(tabStatus).every((v) => v === true);
  })();
  useApiFetch("/report", setreportingData);

  // Modified to handle both report structure loading and saved data
  const fetchReportData = useCallback(async () => {
    if (!supplier_id) return

    setIsPreloadingData(true)

    try {
      // First load the report structure
      const reportStructure = await axios.get(`${API_BASE_URL}/report`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
      })

      if (reportStructure.status === 200) {
        dispatch(setreportingData(reportStructure.data))
      }

      // Then load saved data for the pillars
      const savedDataResponse = await axios.get(
        `${API_BASE_URL}/reportsBySupplier/getReportsBySupplier/${supplier_id}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        },
      )

      if (savedDataResponse.status === 200 && savedDataResponse.data) {
        // Filter for Energy, Waste, and Packaging pillars, plus any other pillars that might be saved
        const pillarsData = savedDataResponse.data.filter((pillar) =>
          ["Energy", "Waste", "Packaging", "CDP"].includes(pillar.pillarId),
        )

        if (pillarsData.length > 0) {
          dispatch(populateReportForm(pillarsData))

          // Mark pillars as saved
          const newSavedPillars = {}
          pillarsData.forEach((pillar) => {
            newSavedPillars[pillar.pillarId] = true
          })
          setSavedPillars((prev) => ({ ...prev, ...newSavedPillars }))
        }
      }
    } catch (error) {
      console.error("Error fetching report data:", error)
      // notification.error({
      //   message: "Error Loading Data",
      //   description: "Failed to load report data. Please try again.",
      // })
    } finally {
      setIsPreloadingData(false)
      dispatch(setLoading(false))
    }
  }, [supplier_id, dispatch])

  // Replace useApiFetch with our custom combined fetch
  useEffect(() => {
    fetchReportData()
  }, [fetchReportData])

  const activeTabs = Object.keys(savedFocusAreas).filter((key) => savedFocusAreas[key]);
  console.log("activeTabs:", activeTabs);

  const activeAreas = Object.keys(savedFocusAreas).filter((key) => savedFocusAreas[key]);
  // Redirect if presurvey is not submitted
  useEffect(() => {
    if (!isPresurveySubmitted) {
      window.location.href = '';
    }
  }, [isPresurveySubmitted]);

  const validateQuestionsSelected = (switchState, currentPillarData) => {
    const hasNonTableQuestions = currentPillarData?.reportQuestions?.some(q => q.type !== "TABLE");
    
    if (!hasNonTableQuestions) {
      return true;
    }
    
    return Object.values(switchState).some(isSelected => isSelected);
  };

  const validateQuestions = (reportFormState, switchState, emissionSaveState) => {
    return Object.keys(switchState).every((questionId) => {
      if (!switchState[questionId]) return true;

      const relatedQuestions = Object.keys(reportFormState).filter(key => 
        key === questionId || (key.startsWith(questionId) && key.includes('_duplicate_'))
      );
      
      return relatedQuestions.every(qId => {
        const inputFields = reportFormState[qId]?.inputFields || {};
        const hasEmissionValue = emissionSaveState[qId];

        if (Object.keys(inputFields).length === 0) return false;

        // Enhanced validation to check all fields including additional ones
        const allFieldsFilled = Object.entries(inputFields).every(([key, value]) => {
          // Skip validation for total fields
          if (key.toLowerCase().includes('total')) return true;
          return value !== null && value !== undefined && value !== '';
        });

        return allFieldsFilled && hasEmissionValue;
      });
    });
  };

  const validateTables = (reportFormState, currentPillarData) => {
    const tableQuestions = currentPillarData?.reportQuestions?.filter(q => q.type === "TABLE") || [];
    
    return tableQuestions.every(tableQuestion => {
      const tableData = reportFormState[tableQuestion.questionId]?.inputFields || {};
      
      if (Object.keys(tableData).length === 0) {
        return false;
      }

      return Object.entries(tableData).every(([rowId, rowData]) => {
        if (!rowId.startsWith('row')) return true;

        return tableQuestion.inputFields.every(field => {
          if (field.tableHeader === "Total Emissions") return true;
          
          const value = rowData[field.inputId];
          return value !== null && value !== undefined && value !== '';
        });
      });
    });
  };

  const handleSaveClick = useCallback(async () => {
    try {
      const reportFormState = ReportForm[activeTabKey] || {};
      const emissionSaveState = emissionSaved[activeTabKey] || {};
      const switchState = SwitchState[activeTabKey] || {};
      const currentPillarData = reportingData.find(data => data.pillarId === activeTabKey);

      const areQuestionsSelected = validateQuestionsSelected(switchState, currentPillarData);
      if (!areQuestionsSelected) {
        notification.warning({
          message: "No questions have been Answered",
          description: "Please select at least one question before saving.",
        });
        return;
      }
      // if(!canSave){
      //   notification.warning({
      //     message: "Values have have been changed",
      //     description: "Please calculate the changes before saving.",
      //   });
      //   return;
      // }

      const areQuestionsValid = validateQuestions(reportFormState, switchState, emissionSaveState);
      const areTablesValid = validateTables(reportFormState, currentPillarData);

      // if (currentPillarData?.reportQuestions?.some(q => q.type !== "TABLE") && !areQuestionsValid) {
      //   notification.warning({
      //     message: NOTIFICATION_MESSAGES.MISSING_FIELDS_TITLE,
      //     // description: "Please Answer all the question and make sure calculations are done",
      //   });
      //   return;
      // }

      const shouldSkipCalculationCheck = activeTabKey === 'Waste' || 
        Object.keys(switchState).filter(id => switchState[id]).every(id => {
        
          if (!reportFormState[id]) return true;
          
          return emissionSaveState[id] !== undefined && emissionSaveState[id] !== null && emissionSaveState[id] !== '';
        });

      // if (!shouldSkipCalculationCheck) {
      //   notification.warning({
      //     message: "Values have been changed.",
      //     description: "Calculate before saving.",
      //   });
      //   return;
      // }

      if (!areTablesValid) {
        notification.warning({
          message: NOTIFICATION_MESSAGES.MISSING_FIELDS_TITLE,
          description: "Please complete all required fields in the tables.",
        });
        return;
      }

      const payload = {
        supplierId: supplier_id,
        pillarId: activeTabKey,
        year: "2025",
        status: "In Progress/Submitted",
        created_dt: "",
        created_user: "",
        updated_dt: "",
        updated_user: "",
        actualResponses: Object.keys(reportFormState).map((questionId) => {
          const inputFields = reportFormState[questionId]?.inputFields || {};
          const isTableInput = Object.keys(inputFields).some(key => key.startsWith('row'));

          let responses;
          if (isTableInput) {
            responses = Object.entries(inputFields).map(([rowId, rowData]) => ({
              responseId: rowId,
              emissionSaved: emissionSaveState[questionId] || "",
              response: Object.entries(rowData).map(([fieldId, value]) => ({
                fieldId: fieldId,
                value: value || "",
              })),
            }));
          } else {
            responses = [{
              responseId: "",
              emissionSaved: emissionSaveState[questionId] || "",
              response: Object.entries(inputFields).map(([fieldId, value]) => ({
                fieldId: fieldId,
                value: value || "",
              })),
            }];
          }

          return {
            questionId: questionId,
            isSelected: switchState[questionId] || false,
            totalEmissionSaved: emissionSaveState[questionId] || "",
            responses: responses,
          };
        }),
      };

      const handleSave = async () => { 
        try {
          const response = await axios.post(
            API_BASE_URL + API_ENDPOINTS.SAVE_REPORTS_BY_SUPPLIER, 
            payload,  {
              headers: {
                  "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,   
                  "Content-Type": "application/json"
              }
          }
          );
      
          if (response.status === 200) {
            dispatch(setisSaved(true));
            
            // Update saved pillars state
            setSavedPillars(prev => ({
              ...prev,
              [activeTabKey]: true
            }));

            notification.success({
              message: NOTIFICATION_MESSAGES.SAVE_SUCCESS_TITLE,
              description: NOTIFICATION_MESSAGES.SAVE_SUCCESS_DESC,
            });
          }
        } catch (error) {
          console.error('Error saving report:', error);
          dispatch(setisSaved(false));
          notification.error({
            message: NOTIFICATION_MESSAGES.SAVE_FAILURE_TITLE,
            description: NOTIFICATION_MESSAGES.SAVE_FAILURE_DESC,
          });
        }
      };

      confirm({
        title: AlertMessages.SAVE_CONFIRMATION,
        okText: "Yes",
        cancelText: "No",
        async onOk() {
          await handleSave();
        },
      });

    } catch (error) {
      dispatch(setisSaved(false));
      console.error('Error saving report:', error);
    }
  }, [activeTabKey, ReportForm, emissionSaved, SwitchState, supplier_id, dispatch, reportingData]);

  const handleResetClick = useCallback(() => {
    const handleReset = async () => {
      try {
        const response=await axios.post(`https://techsolutionsservice.ustdev.com/reportsBySupplier/reset/${emailId}/${activeTabKey}`,{}, {
          headers: {
              "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,   
              "Content-Type": "application/json"
          }
      })
        dispatch(resetReportForm({ pillarId: activeTabKey }));
        dispatch(resetEmissionSaved({ pillarId: activeTabKey }));
        dispatch(resetSwitchState({ pillarId: activeTabKey }));
        dispatch(setisReset(true));
  
        // Remove saved status for current pillar
        setSavedPillars(prev => {
          const newSavedPillars = {...prev};
          delete newSavedPillars[activeTabKey];
          return newSavedPillars;
        });
      } catch (error) {
        console.log("some error occured",error)
      }
    };

    confirm({
      title: AlertMessages.RESET_CONFIRMATION,
      okText: SWITCH_TEXT.YES,
      cancelText: SWITCH_TEXT.NO,
      onOk() {
        handleReset();
      },
    });
  }, [activeTabKey, dispatch]);
  console.log("savedFocusAreas:", typeof savedFocusAreas);


  const handleNextClick = useCallback(() => {
    // Add validation before allowing next
    const reportFormState = ReportForm[activeTabKey] || {};
    const emissionSaveState = emissionSaved[activeTabKey] || {};
    const switchState = SwitchState[activeTabKey] || {};
    const currentPillarData = reportingData.find(data => data.pillarId === activeTabKey);

    // Check if current pillar is saved before allowing navigation
    if (!savedPillars[activeTabKey]) {
      notification.warning({
        message: "Pillar Not Saved",
        description: "Please save the current pillar before proceeding.",
      });
      return;
    }

    const currentIndex = reportingData.findIndex((item) => item.pillarId === activeTabKey);
    const nextIndex = (currentIndex + 1) % reportingData.length;
    dispatch(setactiveTabKey(reportingData[nextIndex].pillarId));
    dispatch(setisSaved(false));
  }, [activeTabKey, reportingData, dispatch, savedPillars]);

  // Modify tab change to prevent switching if current pillar is not saved
  const handleTabChange = (key) => {
    // If trying to switch to a different pillar and current pillar is not saved
    if (key !== activeTabKey) {
      // Allow switching to already saved pillars
      if (savedPillars[key]) {
        dispatch(setactiveTabKey(key));
        return;
      }

      // Prevent switching from unsaved current pillar to a different unsaved pillar
      if (!savedPillars[activeTabKey]) {
        notification.warning({
          message: "Unsaved Changes",
          description: "Please save the current pillar before switching.",
        });
        return;
      }
    }

    // If all checks pass, change the active tab
    dispatch(setactiveTabKey(key));
  };

  useEffect(() => {
    const firstActivePillar = Object.entries(savedFocusAreas).find(([pillarId, isActive]) =>
      isActive
    )?.[0];
 
    if (firstActivePillar) {
      dispatch(setactiveTabKey(firstActivePillar));
    }
  }, [savedFocusAreas, dispatch]);

  const items = useMemo(() =>
    reportingData.map((reportData) => {
      if (savedFocusAreas[reportData.pillarId]) {
        return {
          key: reportData.pillarId,
          label: (
            <h1
              className={`pb-0 text-[#1F405A] font-bold ${
                activeTabKey === reportData.pillarId
                  ? "border-[#1F405A] border-b-[3px]"
                  : ""
              }`}
            >
              {reportData.pillarId}
            </h1>
          ),
          children: <TabsGenerator Data={reportData} />
        };
      }
      return null;
    }).filter(Boolean),
    [reportingData, activeTabKey, savedFocusAreas, savedPillars]
  );

  
  
  
  if (!isPresurveySubmitted) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-auto bg-white">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-600">Loading ...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Tabs
        activeKey={activeTabKey}
        items={items}
        onChange={handleTabChange}
        tabBarStyle={{
          backgroundColor: 'white',
          color: '#1F405A',
          height: '45px',
          fontSize: "bold",
          margin: 0,
        }}
        className="customizing-tabs bg-white w-auto h-auto"
        tabBarGutter={0}
      />
      <br />
      <div className="footer-buttons-container w-auto flex justify-end mr-[50px] gap-2.5">
        <ResetButton onClick={handleResetClick} />
        <SaveButton onClick={handleSaveClick}  />
        {activeTabs.indexOf(activeTabKey) < activeTabs.length - 1 ? 
 <NextButton isSaved={savedPillars[activeTabKey] || false} onClick={handleNextClick} /> 
  : null}

      </div>
      <br />
    </div>
  );
};

export default Report;